import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, SUPABASE_FUNCTIONS_URL } from '../../lib/supabase';
import { posthog } from '../../lib/posthog';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import type { UserProfile, SavingsHistoryEntry } from '../../lib/types';

interface BillingSectionProps {
  sessionToken: string;
  profile: UserProfile | null;
  onProfileRefresh: () => void;
}

const BRAND_DISPLAY: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

function formatBrand(brand: string): string {
  return BRAND_DISPLAY[brand] || brand.charAt(0).toUpperCase() + brand.slice(1);
}

function cleanUrlParams(...keys: string[]) {
  const url = new URL(window.location.href);
  for (const key of keys) url.searchParams.delete(key);
  window.history.replaceState({}, '', url.pathname + url.search);
}

export function BillingSection({ sessionToken, profile, onProfileRefresh }: BillingSectionProps) {
  const hasCard = !!(profile?.card_last4 && profile?.card_brand);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const hasHandledRedirect = useRef(false);

  // Savings history
  const [savingsHistory, setSavingsHistory] = useState<SavingsHistoryEntry[]>([]);
  const [savingsLoading, setSavingsLoading] = useState(true);
  const [hasPendingCharges, setHasPendingCharges] = useState(false);

  const loadSavingsHistory = useCallback(async () => {
    setSavingsLoading(true);
    try {
      // Fetch savings events with commission ledger status
      const { data: events, error: eventsError } = await supabase
        .from('savings_events')
        .select('id, booking_id, original_price, new_price, savings_amount, commission_amount, net_savings, currency, is_award_booking, route, airline_code, created_at')
        .order('created_at', { ascending: false });

      if (eventsError || !events) {
        setSavingsHistory([]);
        return;
      }

      // Fetch commission ledger statuses
      const eventIds = events.map(e => e.id);
      const { data: ledgerEntries } = await supabase
        .from('commission_ledger')
        .select('savings_event_id, status')
        .in('savings_event_id', eventIds);

      const statusMap = new Map<string, string>();
      for (const entry of ledgerEntries ?? []) {
        statusMap.set(entry.savings_event_id, entry.status);
      }

      const history: SavingsHistoryEntry[] = events.map(e => ({
        ...e,
        charge_status: statusMap.get(e.id) || 'unknown',
      }));

      setSavingsHistory(history);
      setHasPendingCharges(history.some(h => h.charge_status === 'pending' || h.charge_status === 'failed'));
    } catch {
      setSavingsHistory([]);
    } finally {
      setSavingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSavingsHistory();
  }, [loadSavingsHistory]);

  // Handle checkout return from Stripe
  useEffect(() => {
    if (hasHandledRedirect.current) return;
    const params = new URLSearchParams(window.location.search);

    const checkoutSessionId = params.get('checkout_session_id');
    if (checkoutSessionId) {
      hasHandledRedirect.current = true;
      setVerifying(true);

      fetch(`${SUPABASE_FUNCTIONS_URL}/verify-checkout-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: checkoutSessionId }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('Verification failed');
          posthog.capture('billing_card_updated', { source: 'settings' });
          cleanUrlParams('checkout_session_id');
          onProfileRefresh();
        })
        .catch(() => {
          setError('Failed to verify your payment method. Please try again.');
          cleanUrlParams('checkout_session_id');
        })
        .finally(() => setVerifying(false));
      return;
    }

    if (params.has('checkout_cancelled')) {
      hasHandledRedirect.current = true;
      cleanUrlParams('checkout_cancelled');
    }
  }, [sessionToken, onProfileRefresh]);

  const handleUpdateCard = async () => {
    setUpdating(true);
    setError(null);

    try {
      // Use the query-param form directly so Stripe lands the user on the
      // URL that actually mounts BillingSection. Skipping the /settings/billing
      // → /?settings=billing redirect hop means one less place for query
      // params like checkout_session_id to get dropped.
      const returnUrl = `${window.location.origin}/?settings=billing`;
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ app_url: returnUrl }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to start payment setup');
      }

      const { url } = await res.json();
      posthog.capture('billing_update_started', { source: 'settings' });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setUpdating(false);
    }
  };

  if (verifying) {
    return (
      <div className="flex items-center gap-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-stone-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-stone-500"></span>
        </span>
        <span className="text-sm text-stone-500 dark:text-stone-400">
          Confirming your payment method&hellip;
        </span>
      </div>
    );
  }

  // Compute totals
  const totalGross = savingsHistory.reduce((sum, e) => sum + e.savings_amount, 0);
  const totalCommission = savingsHistory.reduce((sum, e) => sum + e.commission_amount, 0);
  const totalNet = savingsHistory.reduce((sum, e) => sum + e.net_savings, 0);

  return (
    <div className="space-y-8">
      {/* Payment Method */}
      <div>
        <label className="block text-xs font-mono tracking-wide text-stone-400 dark:text-stone-500 uppercase mb-3">
          Payment Method
        </label>

        {hasCard ? (
          <div className="flex items-center justify-between p-4 bg-stone-100 dark:bg-stone-800/50 rounded-xl mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-7 bg-white dark:bg-stone-700 rounded flex items-center justify-center">
                <span className="text-[10px] font-bold text-stone-600 dark:text-stone-300 uppercase">
                  {profile!.card_brand === 'visa' ? 'VISA' :
                   profile!.card_brand === 'mastercard' ? 'MC' :
                   profile!.card_brand === 'amex' ? 'AMEX' :
                   profile!.card_brand!.slice(0, 4).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-stone-900 dark:text-stone-100">
                  {formatBrand(profile!.card_brand!)} ending in {profile!.card_last4}
                </p>
                <p className="text-xs text-stone-400 dark:text-stone-500">
                  Expires {profile!.card_exp_month!.toString().padStart(2, '0')}/{profile!.card_exp_year}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">
            No payment method on file.
          </p>
        )}

        {error && (
          <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-3 mb-4">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <button
          onClick={handleUpdateCard}
          disabled={updating}
          className="px-4 py-2 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-sm font-medium rounded-full hover:opacity-90 disabled:opacity-50"
        >
          {updating ? 'Connecting to Stripe\u2026' : hasCard ? 'Update Card' : 'Add Card'}
        </button>

        <p className="text-xs text-stone-400 dark:text-stone-500 mt-4">
          Perch fee: 15% of what we save you. You keep the rest as airline credit.
        </p>
      </div>

      {/* Failed charge banner */}
      {hasPendingCharges && (
        <div className="bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-500 rounded-lg p-4" role="alert" aria-live="polite">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Your card couldn't be charged. Please{' '}
            <button onClick={handleUpdateCard} className="font-medium underline hover:no-underline">
              update your payment method
            </button>.
          </p>
        </div>
      )}

      {/* Savings History */}
      <div>
        <label className="block text-xs font-mono tracking-wide text-stone-400 dark:text-stone-500 uppercase mb-4">
          Savings History
        </label>

        {savingsLoading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-stone-200 dark:bg-stone-800 rounded-lg" />
            ))}
          </div>
        ) : savingsHistory.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-8 h-8 text-stone-300 dark:text-stone-600 mx-auto mb-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
            </svg>
            <p className="text-sm font-medium text-stone-600 dark:text-stone-400 mb-1">
              Your Savings Will Appear Here
            </p>
            <p className="text-xs text-stone-400 dark:text-stone-500 max-w-xs mx-auto">
              When we find a lower fare and rebook your flight, your airline issues a travel credit for the difference. You'll see each rebook here — savings, Perch fee, and credit type.
            </p>
          </div>
        ) : (
          <div>
            {/* Ledger rows */}
            <table className="w-full" role="table" aria-label="Savings history">
              <thead className="sr-only">
                <tr>
                  <th>Date</th>
                  <th>Route</th>
                  <th>Saved</th>
                  <th>Perch Fee</th>
                  <th>Net</th>
                </tr>
              </thead>
              <tbody>
                {savingsHistory.map(entry => (
                  <tr key={entry.id} className="border-b border-stone-200/60 dark:border-stone-700/50">
                    <td className="py-3 pr-3 text-sm text-stone-500 dark:text-stone-400 whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="py-3 pr-3 font-mono text-sm text-stone-900 dark:text-stone-100">
                      {entry.route || '—'}
                    </td>
                    <td className="py-3 pr-3 text-sm text-stone-600 dark:text-stone-400 whitespace-nowrap hidden sm:table-cell">
                      {entry.is_award_booking ? `${Math.round(entry.savings_amount)} pts` : `$${Math.round(entry.savings_amount)}`}
                    </td>
                    <td className="py-3 pr-3 text-sm text-stone-400 dark:text-stone-500 whitespace-nowrap hidden sm:table-cell">
                      {entry.commission_amount > 0 ? `$${Math.round(entry.commission_amount)}` : entry.is_award_booking ? 'Free' : '—'}
                    </td>
                    <td className="py-3 text-right font-data tabular-nums text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                      {entry.is_award_booking
                        ? `${Math.round(entry.net_savings)} pts`
                        : `$${Math.round(entry.net_savings)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
              {savingsHistory.length > 1 && (
                <tfoot>
                  <tr className="border-t-2 border-stone-300 dark:border-stone-600">
                    <td className="py-3 pr-3 text-sm font-medium text-stone-700 dark:text-stone-300">Total</td>
                    <td className="py-3 pr-3"></td>
                    <td className="py-3 pr-3 text-sm text-stone-600 dark:text-stone-400 hidden sm:table-cell">
                      ${Math.round(totalGross)}
                    </td>
                    <td className="py-3 pr-3 text-sm text-stone-400 dark:text-stone-500 hidden sm:table-cell">
                      ${Math.round(totalCommission)}
                    </td>
                    <td className="py-3 text-right font-data tabular-nums text-lg text-emerald-600 dark:text-emerald-400">
                      ${Math.round(totalNet)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Referral */}
      {profile?.referral_code && (
        <ReferralSection referralCode={profile.referral_code} hasWaiver={profile.commission_waived_next} />
      )}
    </div>
  );
}

function ReferralSection({ referralCode, hasWaiver }: { referralCode: string; hasWaiver: boolean }) {
  const { copied, copy } = useCopyToClipboard(referralCode);

  return (
    <div>
      <label className="block text-xs font-mono tracking-wide text-stone-400 dark:text-stone-500 uppercase mb-3">
        Refer a Friend
      </label>

      <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
        Share your code. When a friend signs up, your next rebook commission is waived.
      </p>

      <div className="flex items-center gap-3 p-4 bg-stone-100 dark:bg-stone-800/50 rounded-xl mb-3">
        <code className="flex-1 text-lg font-mono font-semibold text-stone-900 dark:text-stone-100 tracking-wider">
          {referralCode}
        </code>
        <button
          onClick={copy}
          className={`px-4 py-2 text-sm font-medium rounded-full transition-[background-color,color] ${
            copied
              ? 'bg-emerald-500 text-white'
              : 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90'
          }`}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {hasWaiver && (
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/50 dark:border-emerald-800/30 p-3">
          <p className="text-sm text-emerald-700 dark:text-emerald-300">
            Your next cash rebook commission is waived — thanks for referring a friend.
          </p>
        </div>
      )}
    </div>
  );
}
